import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Plus, Pencil, Trash2, X, Save, RefreshCw, Search } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { MarketTicker_Legacy } from "@/types/models";
import { mockTickerService } from "@/lib/mockTickerService";
import { formatInr, formatPercent } from "@/lib/numberFormat";
import {
  formatTickerAmountByCurrency,
  formatTickerPriceLabel,
  isUsdInrTicker,
  resolveTickerCurrency,
  toDisplayDirectionalChange,
} from "@/lib/tickerDirection";
import { apiClient } from "@/lib/apiClient";
import TrendPyramidIcon from "@/components/TrendPyramidIcon";

type TickerCurrency = "USD" | "INR" | "NONE";

const parseTickerCurrency = (value: unknown): TickerCurrency => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "INR") return "INR";
  if (normalized === "NONE") return "NONE";
  return "USD";
};

const AdminMarketTicker = () => {
  const navigate = useNavigate();
  const [tickers, setTickers] = useState<MarketTicker_Legacy[]>([]);
  const [editing, setEditing] = useState<MarketTicker_Legacy | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usdAmount, setUsdAmount] = useState(1);
  const [previousRupeeValue, setPreviousRupeeValue] = useState(0);
  const [presentRupeeValue, setPresentRupeeValue] = useState(0);
  const [isSavingUsdInr, setIsSavingUsdInr] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [priceInput, setPriceInput] = useState("0");
  const [manualChangeEdited, setManualChangeEdited] = useState(false);
  const [manualChangePercentEdited, setManualChangePercentEdited] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [yesterdayDate, setYesterdayDate] = useState("");
  const [isFetchingYesterdayRate, setIsFetchingYesterdayRate] = useState(false);
  const [yesterdayRateError, setYesterdayRateError] = useState("");

  const usdInrTicker = tickers.find((ticker) =>
    isUsdInrTicker({ symbol: ticker.symbol, name: ticker.name })
  );
  const nonUsdInrTickers = tickers.filter(
    (ticker) => !isUsdInrTicker({ symbol: ticker.symbol, name: ticker.name })
  );
  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const toSortableTime = (value?: string) => {
    const timestamp = Date.parse(String(value || "").trim());
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const sortedTickers = [...nonUsdInrTickers].sort(
    (a, b) => toSortableTime(b.lastUpdated) - toSortableTime(a.lastUpdated)
  );
  const filteredTickers = !normalizedSearch
    ? sortedTickers
    : sortedTickers.filter((ticker) =>
        [
          ticker.symbol,
          ticker.name,
          ticker.currency,
          ticker.lastUpdated,
          String(ticker.price),
          String(ticker.change),
          String(ticker.changePercent),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );
  const totalPages = Math.max(1, Math.ceil(filteredTickers.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedTickers = filteredTickers.slice((safePage - 1) * pageSize, safePage * pageSize);

  const getApiErrorMessage = (error: unknown) => {
    const status = (error as { status?: number })?.status;
    const message = error instanceof Error ? error.message : "Request failed";

    if (status === 401) {
      return "Session expired or missing auth token. Please sign in again.";
    }
    if (status === 403) {
      return "You do not have permission to manage market ticker.";
    }
    if (status === 404) {
      return "Ticker not found. Refresh data and try again.";
    }
    if (String(message).toLowerCase().includes("data truncated for column 'currency'")) {
      return "Database enum for currency is outdated. Add NONE to market_tickers/mock_ticker_data currency enum in the active DB.";
    }

    return message || "Request failed";
  };

  const getYesterdayDateString = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().slice(0, 10);
  };

  const fetchYesterdayUsdInrRate = async () => {
    const date = getYesterdayDateString();
    setIsFetchingYesterdayRate(true);
    setYesterdayRateError("");
    try {
      const response = await fetch(`https://api.frankfurter.dev/v2/rate/USD/INR?date=${date}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch yesterday rate (${response.status})`);
      }
      const payload = await response.json();
      const rate = Number(payload?.rate);
      if (!Number.isFinite(rate) || rate <= 0) {
        throw new Error("Invalid yesterday USD/INR rate from API");
      }
      setPreviousRupeeValue(Number(rate.toFixed(4)));
      setYesterdayDate(String(payload?.date || date));
    } catch (error) {
      console.error("AdminMarketTicker: Yesterday USD/INR fetch failed:", error);
      setYesterdayRateError("Could not fetch yesterday USD/INR from API.");
    } finally {
      setIsFetchingYesterdayRate(false);
    }
  };

  // Load data from database on component mount
  useEffect(() => {
    const loadMockData = async () => {
      setIsLoading(true);
      try {
        console.log("AdminMarketTicker: Loading mock data from database...");
        const data = await mockTickerService.getAll();
        setTickers(data);
        console.log("AdminMarketTicker: Successfully loaded mock data from database");
      } catch (error) {
        console.error("AdminMarketTicker: Failed to load mock data:", error);
        // Fallback to empty array if database fails
        setTickers([]);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchYesterdayUsdInrRate();
    loadMockData();
  }, []); // Empty dependency array means this runs only once on mount

  useEffect(() => {
    if (!usdInrTicker) {
      return;
    }

    const present = Number(usdInrTicker.price);
    setPresentRupeeValue(Number.isFinite(present) ? present : 0);
  }, [usdInrTicker?.id, usdInrTicker?.price, usdInrTicker?.change]);

  const openNew = () => { 
    setEditing({ 
      id: Date.now().toString(), 
      symbol: "", 
      name: "", 
      currency: "USD",
      price: 0, 
      change: 0, 
      changePercent: 0,
      lastUpdated: new Date().toISOString()
    }); 
    setPriceInput("0");
    setManualChangeEdited(false);
    setManualChangePercentEdited(false);
    setIsNew(true); 
  };
  
  const openEdit = (ticker: MarketTicker_Legacy) => { 
    const resolvedCurrency = parseTickerCurrency(
      resolveTickerCurrency({ symbol: ticker.symbol, name: ticker.name, currency: ticker.currency })
    );
    setEditing({
      ...ticker,
      currency: resolvedCurrency,
    }); 
    setPriceInput(resolvedCurrency === "NONE" && Number(ticker.price) === 0 ? "" : String(Number(ticker.price) || 0));
    setManualChangeEdited(false);
    setManualChangePercentEdited(false);
    setIsNew(false); 
  };

  const handleSave = async () => { 
    if (!editing) return; 

    if (!apiClient.getAuthToken()) {
      alert("Missing auth token. Please sign in again.");
      navigate("/admin/login", { replace: true });
      return;
    }

    if (!String(editing.symbol || "").trim() || !String(editing.name || "").trim()) {
      alert("Symbol and name are required.");
      return;
    }

    const normalizedCurrency: TickerCurrency = isUsdInrTicker({ symbol: editing.symbol, name: editing.name })
      ? "INR"
      : parseTickerCurrency(editing.currency);
    const useManualChangeValues = manualChangeEdited || manualChangePercentEdited;
    const manualChangeValue = Number(editing.change);
    const manualChangePercentValue = Number(editing.changePercent);

    if (
      useManualChangeValues &&
      (!Number.isFinite(manualChangeValue) || !Number.isFinite(manualChangePercentValue))
    ) {
      alert("Manual change values must be valid numbers.");
      return;
    }

    const trimmedPrice = priceInput.trim();
    const isPriceBlank = trimmedPrice === "";
    const parsedPrice = isPriceBlank ? NaN : Number(trimmedPrice);
    const resolvedPrice =
      normalizedCurrency === "NONE"
        ? Number.isFinite(parsedPrice)
          ? parsedPrice
          : 0
        : parsedPrice;

    if (normalizedCurrency !== "NONE" && (!Number.isFinite(resolvedPrice) || resolvedPrice < 0)) {
      alert("Price is required and must be a valid number.");
      return;
    }

    const payload: MarketTicker_Legacy = {
      ...editing,
      currency: normalizedCurrency,
      price: Number.isFinite(resolvedPrice) ? resolvedPrice : 0,
    };
    
    console.log("AdminMarketTicker: Saving ticker:", payload);
    
    try {
      // Find the existing ticker to compare prices
      const existingTicker = tickers.find((t) => t.id === payload.id);
      
      if (existingTicker && !isNew) {
        // Update case:
        // - Same day edits: keep a stable base (yesterday close) to prevent drift.
        // - New day first edit: use last saved price as yesterday close.
        const oldPrice = Number(existingTicker.price);
        const oldChange = Number(existingTicker.change);
        const newPrice =
          normalizedCurrency === "NONE" && isPriceBlank ? oldPrice : payload.price;
        payload.price = newPrice;
        const now = new Date();
        const previousUpdatedAt = new Date(existingTicker.lastUpdated || "");
        const isSameDay =
          Number.isFinite(previousUpdatedAt.getTime()) &&
          previousUpdatedAt.toDateString() === now.toDateString();

        let referencePrice = isSameDay
          ? oldPrice - (Number.isFinite(oldChange) ? oldChange : 0)
          : oldPrice;

        if (!Number.isFinite(referencePrice) || referencePrice <= 0) {
          referencePrice = oldPrice;
        }

        // Self-heal obviously corrupted USD/INR baseline data.
        const isUsdInr = isUsdInrTicker({ symbol: existingTicker.symbol, name: existingTicker.name });
        if (isUsdInr) {
          const absOldChange = Math.abs(Number(existingTicker.change) || 0);
          const absOldPercent = Math.abs(Number(existingTicker.changePercent) || 0);
          if (absOldChange > 2 || absOldPercent > 5) {
            referencePrice = oldPrice;
          }
        }

        const priceChange = newPrice - referencePrice;
        const changePercent = referencePrice !== 0 ? (priceChange / referencePrice) * 100 : 0;
        
        // Update the editing ticker with calculated values
        if (useManualChangeValues) {
          payload.change = manualChangeValue;
          payload.changePercent = manualChangePercentValue;
        } else {
          payload.change = priceChange;
          payload.changePercent = changePercent;
        }
        payload.lastUpdated = now.toISOString();
        payload.currency = normalizedCurrency;
        
        console.log("AdminMarketTicker: Auto-calculated changes:", {
          oldPrice,
          referencePrice,
          newPrice,
          priceChange,
          changePercent
        });
      } else if (isNew) {
        // New ticker case
        if (useManualChangeValues) {
          payload.change = manualChangeValue;
          payload.changePercent = manualChangePercentValue;
        } else {
          payload.change = 0;
          payload.changePercent = 0;
        }
        payload.lastUpdated = new Date().toISOString();
        payload.currency = normalizedCurrency;
        
        console.log("AdminMarketTicker: New ticker starting from 0% change");
      }
      
      if (isNew) {
        // Add new ticker to database
        const newTicker = await mockTickerService.add(payload);
        setTickers(prev => [...prev, newTicker]);
        console.log("AdminMarketTicker: New ticker saved to database");
      } else {
        // Update existing ticker in database
        const updatedTicker = await mockTickerService.update(payload.id, payload);
        setTickers(prev => prev.map(t => t.id === payload.id ? updatedTicker : t));
        console.log("AdminMarketTicker: Ticker updated in database");
      }
      
      setEditing(null); 
    } catch (error) {
      console.error("AdminMarketTicker: Save failed:", error);
      const message = getApiErrorMessage(error);
      alert(message);

      if ((error as { status?: number })?.status === 401) {
        navigate("/admin/login", { replace: true });
      }
    }
  };

  const handleUsdInrSave = async () => {
    if (!apiClient.getAuthToken()) {
      alert("Missing auth token. Please sign in again.");
      navigate("/admin/login", { replace: true });
      return;
    }

    if (usdAmount <= 0 || previousRupeeValue <= 0 || presentRupeeValue <= 0) {
      alert("USD amount, previous rupee value, and present rupee value must be greater than 0.");
      return;
    }

    setIsSavingUsdInr(true);
    try {
      const price = Number(presentRupeeValue);
      const change = Number(presentRupeeValue) - Number(previousRupeeValue);
      const changePercent = previousRupeeValue !== 0 ? (change / Number(previousRupeeValue)) * 100 : 0;
      const nowIso = new Date().toISOString();

      const payload: MarketTicker_Legacy = {
        id: usdInrTicker?.id || Date.now().toString(),
        symbol: usdInrTicker?.symbol || "FX_IDC:USDINR",
        name: usdInrTicker?.name || "USD/INR",
        currency: "INR",
        price: Number(price.toFixed(4)),
        change: Number(change.toFixed(4)),
        changePercent: Number(changePercent.toFixed(4)),
        lastUpdated: nowIso,
      };

      if (usdInrTicker) {
        const updated = await mockTickerService.update(payload.id, payload);
        setTickers((prev) => prev.map((ticker) => (ticker.id === payload.id ? updated : ticker)));
      } else {
        const created = await mockTickerService.add(payload);
        setTickers((prev) => [...prev, created]);
      }
      await fetchYesterdayUsdInrRate();
    } catch (error) {
      console.error("AdminMarketTicker: USD/INR save failed:", error);
      const message = getApiErrorMessage(error);
      alert(message);
      if ((error as { status?: number })?.status === 401) {
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setIsSavingUsdInr(false);
    }
  };
  
  const handleDelete = async (id: string) => { 
    if (confirm("Delete this ticker item?")) {
      try {
        await mockTickerService.delete(id);
        setTickers(prev => prev.filter(t => t.id !== id));
        console.log("AdminMarketTicker: Ticker deleted from database");
      } catch (error) {
        console.error("AdminMarketTicker: Delete failed:", error);
        const message = getApiErrorMessage(error);
        alert(message);

        if ((error as { status?: number })?.status === 401) {
          navigate("/admin/login", { replace: true });
        }
      }
    }
  };

  const update = (field: keyof MarketTicker_Legacy, value: any) => {
    setEditing((prev) => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  };

  const effectiveEditingCurrency: TickerCurrency =
    editing && isUsdInrTicker({ symbol: editing.symbol, name: editing.name })
      ? "INR"
      : parseTickerCurrency(
          editing ? resolveTickerCurrency({ symbol: editing.symbol, name: editing.name, currency: editing.currency }) : "USD"
        );

  // Refresh data from database
  const refreshData = async () => {
    setIsLoading(true);
    try {
      const data = await mockTickerService.getAll();
      setTickers(data);
      await fetchYesterdayUsdInrRate();
      console.log("AdminMarketTicker: Successfully refreshed data from database");
    } catch (error) {
      console.error("AdminMarketTicker: Refresh failed:", error);
      const message = getApiErrorMessage(error);
      alert(message);

      if ((error as { status?: number })?.status === 401) {
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const rupeeTrend =
    presentRupeeValue > previousRupeeValue
      ? "down"
      : presentRupeeValue < previousRupeeValue
      ? "up"
      : "stable";
  const rupeeTrendPercent =
    previousRupeeValue > 0
      ? Math.abs(((presentRupeeValue - previousRupeeValue) / previousRupeeValue) * 100)
      : 0;
  const rupeeTrendLabel =
    rupeeTrend === "down"
      ? "Rupee Down (weaker)"
      : rupeeTrend === "up"
      ? "Rupee Up (stronger)"
      : "Rupee Stable";
  const rupeeTrendClass =
    rupeeTrend === "down"
      ? "text-red-600"
      : rupeeTrend === "up"
      ? "text-green-600"
      : "text-muted-foreground";
  const previousConvertedValue = usdAmount * previousRupeeValue;
  const presentConvertedValue = usdAmount * presentRupeeValue;
  const usdInrDelta = presentRupeeValue - previousRupeeValue;
  const usdInrDeltaPercent = previousRupeeValue > 0 ? (usdInrDelta / previousRupeeValue) * 100 : 0;
  const usdInrDeltaClass = usdInrDelta > 0 ? "text-green-600" : usdInrDelta < 0 ? "text-red-600" : "text-muted-foreground";
  const trendChartColor =
    rupeeTrend === "down" ? "#dc2626" : rupeeTrend === "up" ? "#16a34a" : "#6b7280";
  const rupeeTrendChartData = [
    {
      label: "Prev",
      rateValue: previousRupeeValue,
      // Plot inverted value so chart direction reflects rupee strength.
      // Higher USD/INR means weaker INR (down), so line should go down.
      trendValue: -previousRupeeValue,
    },
    {
      label: "Now",
      rateValue: presentRupeeValue,
      trendValue: -presentRupeeValue,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-foreground">Manage Market Ticker</h1>
        <div className="flex gap-2">
          <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
            <Plus size={16} /> Add Ticker
          </button>
          <button 
            onClick={refreshData} 
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw size={16} />
            {isLoading ? "Loading..." : "Refresh Data"}
          </button>
        </div>
      </div>
      <form onSubmit={onSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by symbol, name, currency, date..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          <Search size={15} />
          Search
        </button>
      </form>

      <div className="finance-card p-5 mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">USD vs INR</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Yesterday INR value is auto-fetched from API. Enter only today&apos;s present INR value for 1 USD.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">USD Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={usdAmount}
              onChange={(event) => setUsdAmount(parseFloat(event.target.value) || 0)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Previous Rupee Value (for 1 USD){yesterdayDate ? ` - ${yesterdayDate}` : ""}
            </label>
            <input
              type="number"
              step="0.0001"
              min="0"
              value={previousRupeeValue}
              readOnly
              className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-muted/30 text-sm"
            />
            {isFetchingYesterdayRate && (
              <p className="mt-1 text-[11px] text-muted-foreground">Fetching yesterday rate...</p>
            )}
            {yesterdayRateError && (
              <p className="mt-1 text-[11px] text-red-600">{yesterdayRateError}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Present Rupee Value (for 1 USD)</label>
            <input
              type="number"
              step="0.0001"
              min="0"
              value={presentRupeeValue}
              onChange={(event) => setPresentRupeeValue(parseFloat(event.target.value) || 0)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-muted-foreground">
              Previous: {usdAmount.toFixed(2)} USD = {formatInr(previousConvertedValue)}
            </span>
            <span className="text-muted-foreground">
              Present: {usdAmount.toFixed(2)} USD = {formatInr(presentConvertedValue)}
            </span>
            <span className={`font-semibold ${usdInrDeltaClass}`}>
              Change: {usdInrDelta >= 0 ? "+" : ""}{formatInr(usdInrDelta)} ({usdInrDeltaPercent >= 0 ? "+" : ""}{formatPercent(usdInrDeltaPercent)}%)
            </span>
            <span className={`font-semibold ${rupeeTrendClass}`}>{rupeeTrendLabel}</span>
          </div>
        </div>
        <div className="mt-3 rounded-lg border border-border bg-background p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">Rupee Trend Graph</span>
            <span className={`text-xs font-semibold ${rupeeTrendClass}`}>
              {rupeeTrend === "down" ? "-" : rupeeTrend === "up" ? "+" : ""}
              {rupeeTrendPercent.toFixed(2)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            {rupeeTrend === "down" ? (
              <TrendingDown size={18} className="text-red-600" />
            ) : rupeeTrend === "up" ? (
              <TrendingUp size={18} className="text-green-600" />
            ) : (
              <div className="w-[18px] h-[18px] rounded-full bg-muted" />
            )}
            <div className="w-full h-24" aria-label="Rupee trend graph">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={rupeeTrendChartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis hide domain={["auto", "auto"]} />
                  <Tooltip
                    formatter={(_value: number, _name: string, entry: any) => [
                      formatInr(Number(entry?.payload?.rateValue || 0)),
                      "INR per 1 USD",
                    ]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="trendValue"
                    stroke={trendChartColor}
                    fill={trendChartColor}
                    fillOpacity={0.2}
                    strokeWidth={2.5}
                    dot={{ r: 3, stroke: trendChartColor, fill: trendChartColor }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
            <span>Prev \u20B9{previousRupeeValue.toFixed(4)}</span>
            <span>Now \u20B9{presentRupeeValue.toFixed(4)}</span>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleUsdInrSave}
            disabled={isSavingUsdInr}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
          >
            <Save size={15} />
            {isSavingUsdInr ? "Saving USD/INR..." : "Save USD/INR"}
          </button>
        </div>
      </div>

      {/* Current Tickers Display */}
      <div className="finance-card overflow-hidden mb-6">
        <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-border">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <TrendingUp size={18} className="text-purple-600" />
            Live Market Ticker Preview
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            This is how ticker will appear on website. Prices update in real-time as users scroll.
          </p>
        </div>
        
        {/* Ticker Preview */}
        <div className="bg-black border-b border-border overflow-hidden">
          <div className="flex overflow-hidden">
            <div className="flex animate-marquee">
              {tickers.concat(tickers).map((ticker, index) => {
                const tickerIdentity = {
                  symbol: ticker.symbol,
                  name: ticker.name,
                  currency: ticker.currency,
                };
                const { change, changePercent } = toDisplayDirectionalChange(
                  tickerIdentity,
                  ticker.change,
                  ticker.changePercent
                );
                const changeColor = change >= 0 ? 'text-green-400' : 'text-red-400';
                const changeDirection = change >= 0 ? "up" : "down";
                const formattedChange = formatTickerAmountByCurrency(tickerIdentity, Math.abs(change));
                
                return (
                  <div key={`${ticker.id}-${index}`} className="flex items-center gap-4 px-6 py-2 whitespace-nowrap">
                    <span className="text-white font-semibold">{ticker.name}</span>
                    <span className="text-yellow-400 font-bold">
                      {formatTickerPriceLabel(tickerIdentity, ticker.price)}
                    </span>
                    <span className={`${changeColor} font-medium inline-flex items-center gap-1`}>
                      <TrendPyramidIcon direction={changeDirection} className={changeColor} />
                      {formattedChange} ({formatPercent(changePercent)}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Tickers List */}
      <div className="finance-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30 text-xs text-muted-foreground">
          Choose USD, Rupee, or None per ticker. USD/INR is managed in the separate panel above and always uses INR.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-semibold">Symbol</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Currency</th>
                <th className="text-left px-4 py-3 font-semibold">Price</th>
                <th className="text-left px-4 py-3 font-semibold">Change</th>
                <th className="text-left px-4 py-3 font-semibold">Change %</th>
                <th className="text-left px-4 py-3 font-semibold">Last Updated</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTickers.map((ticker) => {
                const tickerIdentity = {
                  symbol: ticker.symbol,
                  name: ticker.name,
                  currency: ticker.currency,
                };
                const { change, changePercent } = toDisplayDirectionalChange(
                  tickerIdentity,
                  ticker.change,
                  ticker.changePercent
                );
                const currency = resolveTickerCurrency(tickerIdentity);

                return (
                  <tr key={ticker.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">{ticker.symbol}</td>
                    <td className="px-4 py-3 font-medium">{ticker.name}</td>
                    <td className="px-4 py-3 font-medium">
                      {currency === "INR" ? "Rupee" : currency === "NONE" ? "None" : "USD"}
                    </td>
                    <td className="px-4 py-3 font-semibold">{formatTickerPriceLabel(tickerIdentity, ticker.price)}</td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        {formatTickerAmountByCurrency(tickerIdentity, Math.abs(change))}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {changePercent >= 0 ? '+' : ''}{formatPercent(changePercent)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {new Date(ticker.lastUpdated).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(ticker)} className="p-1.5 rounded hover:bg-muted">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => handleDelete(ticker.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {filteredTickers.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">No ticker rows found.</div>
      )}
      {filteredTickers.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredTickers.length)} of {filteredTickers.length}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {isNew ? "Add New Ticker" : "Edit Ticker"}
              </h3>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Symbol</label>
                <input 
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm font-mono" 
                  value={editing.symbol} 
                  onChange={(e) => update("symbol", e.target.value)} 
                  placeholder="e.g., BSE:SENSEX"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <input 
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" 
                  value={editing.name} 
                  onChange={(e) => update("name", e.target.value)} 
                  placeholder="e.g., Sensex"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Currency</label>
                <select
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={effectiveEditingCurrency}
                  onChange={(e) => {
                    const nextCurrency = parseTickerCurrency(e.target.value);
                    update("currency", nextCurrency);
                    if (nextCurrency === "NONE" && Number(editing.price) === 0) {
                      setPriceInput("");
                    } else if (priceInput.trim() === "") {
                      setPriceInput("0");
                    }
                  }}
                  disabled={isUsdInrTicker({ symbol: editing.symbol, name: editing.name })}
                >
                  <option value="USD">USD ($)</option>
                  <option value="INR">Rupee (?)</option>
                  <option value="NONE">None</option>
                </select>
                {isUsdInrTicker({ symbol: editing.symbol, name: editing.name }) && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    USD/INR ticker always uses Rupee.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Price (
                  {effectiveEditingCurrency === "INR"
                    ? "Rupee ?"
                    : effectiveEditingCurrency === "NONE"
                    ? "No currency"
                    : "USD $"}
                  {effectiveEditingCurrency === "NONE" ? ", Optional" : ""})
                </label>
                <input 
                  type="number"
                  step="0.01"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" 
                  value={priceInput}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setPriceInput(nextValue);
                    const numeric = Number(nextValue);
                    update("price", Number.isFinite(numeric) ? numeric : 0);
                  }}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Change (
                  {effectiveEditingCurrency === "INR"
                    ? "Rupee ?"
                    : effectiveEditingCurrency === "NONE"
                    ? "No currency"
                    : "USD $"}
                  , Auto-calculated / Editable)
                </label>
                <input 
                  type="number"
                  step="0.01"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={editing.change}
                  onChange={(e) => {
                    const numeric = Number(e.target.value);
                    update("change", Number.isFinite(numeric) ? numeric : 0);
                    setManualChangeEdited(true);
                  }}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Change % (Auto-calculated / Editable)</label>
                <input 
                  type="number"
                  step="0.01"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={editing.changePercent}
                  onChange={(e) => {
                    const numeric = Number(e.target.value);
                    update("changePercent", Number.isFinite(numeric) ? numeric : 0);
                    setManualChangePercentEdited(true);
                  }}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex gap-3 p-6 pt-0">
              <button onClick={handleSave} className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2">
                <Save size={16} />
                Save
              </button>
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 rounded-lg bg-muted text-foreground font-semibold text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Marquee Animation Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            display: flex;
            animation: marquee 30s linear infinite;
          }
          .animate-marquee:hover {
            animation-play-state: paused;
          }
        `
      }} />
    </div>
  );
};

export default AdminMarketTicker;









